/**
 * Turning what the server refused into something a form can show.
 *
 * Every form here validated on the client, posted, and — when the server
 * disagreed anyway — showed one flat toast. It could not do better: the API
 * layer discarded express-validation's `details` before any caller saw it, so
 * "which field" and "why" never left services/api.js.
 *
 * Onboarding an employee was the case that made it obvious. Leaving the one
 * optional field untouched sent `employmentType: ""`, which Joi refuses for a
 * string, and the form reported "Please fill in all the required fields" while
 * highlighting none of them — sending an admin hunting through a form that was,
 * as far as it knew, complete.
 *
 * Two rules fall out of that, and they are what this file is:
 *   - Do not send blank optionals. An empty string is not "no value"; it is a
 *     value the server has every right to reject.
 *   - When the server names a field, point at that field.
 */

/**
 * Drop keys with nothing in them, so an untouched optional field is absent
 * rather than empty. `false` and `0` are values and survive.
 */
export const stripEmpty = (payload = {}) =>
  Object.fromEntries(
    Object.entries(payload).filter(
      ([, v]) => v !== "" && v !== null && v !== undefined && !(Array.isArray(v) && v.length === 0)
    )
  );

// express-validation: { details: { body: [ { message, path: ["employmentType"] } ] } }
const detailEntries = (res) => {
  const details = res?.details;
  if (!details || typeof details !== "object") return [];
  return Object.values(details)
    .filter(Array.isArray)
    .flat()
    .filter((d) => d && (d.message || d.path));
};

/**
 * `{ fieldName: "why" }` for whatever the server objected to, ready to merge
 * into a form's error state so the offending input is highlighted.
 */
export const fieldErrorsFrom = (res) => {
  const errors = {};
  detailEntries(res).forEach((d) => {
    const field = Array.isArray(d.path) ? d.path[0] : d.path;
    if (!field) return;
    // Joi quotes the field name back at you — '"employmentType" is not allowed
    // to be empty' — which reads badly next to the input it is already under.
    errors[field] = String(d.message || "")
      .replace(new RegExp(`^"${field}"\\s*`), "")
      .replace(/^"|"$/g, "")
      .trim() || "This value was rejected";
  });
  return errors;
};

/**
 * The single line to show when there is nowhere better to put it. Prefers the
 * server's own words over a generic phrase, because "Validation Failed" tells
 * somebody staring at a full form precisely nothing.
 */
export const errorMessageFrom = (res, fallback = "Something went wrong") => {
  const entries = detailEntries(res);
  if (entries.length) {
    const named = Object.entries(fieldErrorsFrom(res));
    if (named.length === 1) {
      const [field, why] = named[0];
      return `${humanFieldName(field)} ${why}`;
    }
    return `Check these: ${named.map(([f]) => humanFieldName(f)).join(", ")}`;
  }
  const message = res?.message;
  if (!message || message === "Validation Failed") return fallback;
  return message;
};

/**
 * An Error that still knows what the server said.
 *
 * The common shape in this codebase is
 *   if (res?.success === false) throw new Error(res?.message);
 * which throws away the response — so by the time the catch runs, `details`
 * is gone and the only thing left is "Validation Failed". This keeps the
 * readable sentence as the message and carries the field errors along, so a
 * catch can highlight inputs as well as tell somebody what happened.
 */
export const apiError = (res, fallback = "Something went wrong") => {
  const error = new Error(errorMessageFrom(res, fallback));
  error.fieldErrors = fieldErrorsFrom(res);
  return error;
};

// employmentType -> "Employment type". Sentence case, not Title Case: this
// sits inside a sentence, and "Employment Type is not allowed to be empty"
// reads like a product name.
export const humanFieldName = (field = "") =>
  field
    .replace(/([a-z0-9])([A-Z])/g, (_, a, b) => `${a} ${b.toLowerCase()}`)
    .replace(/^./, (c) => c.toUpperCase());
